import React, { useEffect } from 'react';
import { compose } from 'redux';
import { Link } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { withRouter } from 'react-router-dom';

import styles from './LandingPage.module.scss';

import config from '../../config/config';
import Button from '../../components/Button/Button';
import RMLConnection from '../../components/RMLConnection/RMLConnection';
import ModalLink from '../../components/Shared/Modal/ModalLink/ModalLink';
import ErrorPage from './ErrorPage';
import LandingErrorMessage from './LandingErrorMessage';
import Loader from '../../components/Loader/Loader';
import { AUTO_RMD_TOOLD_AND_RESTRICTIONS_MODAL } from '../../redux/modules/modals/modals.module';
import {
  getPersonalInformationData,
  getIsAddressAvailable,
  getIsForeginAddress,
} from '../../redux/modules/PersonalInformation/personalInformation.selectors';
import {
  getTraditionalOrRolloverIraFunds,
  getUsersRetirementFunds,
  getRMDfunds,
  getOnlyOWNRRMDs,
  getHiddenRMDFundIRAs,
} from '../../redux/modules/UserFunds/userFunds.selectors';
import {
  DOB_FAILURE,
  ADDRESS_INCOMPLETE,
  ALL_FUNDS_AUTO_SELL,
  ALL_FUNDS_AUTO_EXCHANGE,
  ALL_FUNDS_AUTO_SELL_EXCHANGE,
  MISSING_USERNAME,
  FOREIGN_ADDRESS,
  RMD_ELIGIBLE_AGE_CHECK,
  RMD_ELIGIBLE_ACCOUNT_CHECK,
  TOLL_FREE_NUMBER,
  DISBURSEMENT_TYPE,
  NO_ELIGIBLE_ACCOUNTS,
  NOT_RMD_ELIGIBLE,
  ZERO_RMD_BALANCE,
  UNAVAILABLE_FOR_REDEMPTION_ONLINE,
  CHECK_SERVICE_FEATURE,
  UP_TO_THREE_HIDDEN_RMD_ELIGIBE_IRA,
  MORE_THAN_THREE_HIDDEN_RMD_ELIGIBE_IRA,
} from '../../settings/settings';
import { GET_STARTED_CLICKED } from '../../redux/modules/shoppingCart/shoppingCart.module';
import { getHasOnlyTheProvidedProductType } from '../../redux/modules/productTypes/productTypes.selectors';
import {
  getApplicationDataIsLoading,
  getApplicationDataHasFailed,
} from '../../redux/modules/applicationData/applicationData.selectors';
import { appendBasePath } from '../../utils/nav.utils';
import { getRMDSummary } from '../../redux/modules/RMD/RMDSetting.selectors';
import { getSalesforceRep } from '../../redux/modules/applicationConfig/applicationConfig.selectors';

const LandingPage = () => {
  useEffect(() => {
    const noop = () => {};
    Object.defineProperty(window, 'scrollTo', { value: noop, writable: true });
    window.scrollTo(0, 0);
  });
  const isLoading = useSelector(getApplicationDataIsLoading);
  const hasError = useSelector(getApplicationDataHasFailed);
  const isSalesforceRep = useSelector((state) => getSalesforceRep(state));
  const personData = useSelector(getPersonalInformationData);
  const hasAddress = useSelector(getIsAddressAvailable);
  const hasForeignAddress = useSelector(getIsForeginAddress);
  const isDisbursementType = useSelector(getOnlyOWNRRMDs);
  // const beneficiaries = useSelector(state => state.RMDSetting.rmdLimitsData ? state.RMDSetting.rmdLimitsData.rmdBeneficiaries : []);
  const isTraditionalOrRolloverIRA = useSelector(
    getTraditionalOrRolloverIraFunds
  );
  const hasOnlyApp = useSelector((state) =>
    getHasOnlyTheProvidedProductType(state, { productType: 'hasAPP' })
  );
  const hasOnlyBrokerage = useSelector((state) =>
    getHasOnlyTheProvidedProductType(state, { productType: 'hasBrokerage' })
  );
  const userFunds = useSelector((state) => getUsersRetirementFunds(state));
  const rmdInfo = useSelector((state) => getRMDSummary(state));
  const hiddenRMDFundIRAs = useSelector((state) => getHiddenRMDFundIRAs(state));

  const dispatch = useDispatch();
  const {
    ROLLOVER_IRA: { unlockedFunds: rolloverUnLockedFunds },
    TRADITIONAL_IRA: { unlockedFunds: traditionalUnLockedFunds },
  } = useSelector(getRMDfunds);

  const getError = () => {
    /* Missing username error */
    if (!personData.retailEnabled) {
      return MISSING_USERNAME;
    }

    /* User is not RMD eligible due to account types */
    if (!isTraditionalOrRolloverIRA || hasOnlyBrokerage || hasOnlyApp) {
      return RMD_ELIGIBLE_ACCOUNT_CHECK;
    }

    /* User is not RMD eligible due to age error */
    if (isTraditionalOrRolloverIRA && !personData.eligibleForRMD) {
      return RMD_ELIGIBLE_AGE_CHECK;
    }

    /* Missing date of birth error */
    if (!personData.dateOfBirth) {
      return DOB_FAILURE;
    }

    /* Foreign Address error */
    if (!hasForeignAddress) {
      return FOREIGN_ADDRESS;
    }
    /* Incomplete Address error */
    if (!hasAddress) {
      return ADDRESS_INCOMPLETE;
    }

    /*Check for LE client error */
    if (isDisbursementType && isDisbursementType.length > 0) {
      return DISBURSEMENT_TYPE;
    }

    /* All funds have either Automatic Sell or Automatic Exchange */
    if (
      (userFunds.TRADITIONAL_IRA.some((fund) => fund.hasAutoSell) ||
        userFunds.ROLLOVER_IRA.some((fund) => fund.hasAutoSell)) &&
      (userFunds.TRADITIONAL_IRA.some((fund) => fund.hasAutoExchange) ||
        userFunds.ROLLOVER_IRA.some((fund) => fund.hasAutoExchange))
    ) {
      return ALL_FUNDS_AUTO_SELL_EXCHANGE;
    }

    /* All funds already have Automatic Sell */
    if (
      userFunds.TRADITIONAL_IRA.some((fund) => fund.hasAutoSell) ||
      userFunds.ROLLOVER_IRA.some((fund) => fund.hasAutoSell)
    ) {
      return ALL_FUNDS_AUTO_SELL;
    }

    if (
      userFunds?.TRADITIONAL_IRA?.every((fund) =>
        fund?.fundLock?.missingRequiredFeatures?.some(
          (agreement) =>
            agreement?.serviceID === UNAVAILABLE_FOR_REDEMPTION_ONLINE
        )
      ) &&
      userFunds?.ROLLOVER_IRA?.every((fund) =>
        fund?.fundLock?.missingRequiredFeatures?.some(
          (agreement) =>
            agreement?.serviceID === UNAVAILABLE_FOR_REDEMPTION_ONLINE
        )
      )
    ) {
      return CHECK_SERVICE_FEATURE;
    }
    /* All funds already have Automatic Exchange */
    if (
      userFunds.TRADITIONAL_IRA.some((fund) => fund.hasAutoExchange) ||
      userFunds.ROLLOVER_IRA.some((fund) => fund.hasAutoExchange)
    ) {
      return ALL_FUNDS_AUTO_EXCHANGE;
    }
    /* Check for unlocked IRA funds */
    if (
      rolloverUnLockedFunds.length === 0 &&
      traditionalUnLockedFunds.length === 0
    ) {
      return NO_ELIGIBLE_ACCOUNTS;
    }

    /* Check for RMD total balance */
    if (rmdInfo && rmdInfo.totalBalance === 0) {
      return ZERO_RMD_BALANCE;
    }

    if (!rmdInfo.eligibleForRMD) {
      return NOT_RMD_ELIGIBLE;
    }
    if (hiddenRMDFundIRAs.length > 0 && hiddenRMDFundIRAs.length <= 3) {
      return UP_TO_THREE_HIDDEN_RMD_ELIGIBE_IRA;
    }

    if (hiddenRMDFundIRAs.length > 3) {
      return MORE_THAN_THREE_HIDDEN_RMD_ELIGIBE_IRA;
    }
    return null;
  };

  if (isLoading) {
    return (
      <div className={styles.loading}>
        <Loader isVisible={true} />
      </div>
    );
  }

  if (hasError) {
    return <ErrorPage />;
  }

  /* if (personData && !personData.eligibleForRMD) {
    return <div>You are not eligible for RMD</div>;
  } */

  const errorType = getError();
  const autoCheck =
    errorType !== ALL_FUNDS_AUTO_SELL_EXCHANGE &&
    errorType !== ALL_FUNDS_AUTO_SELL &&
    errorType !== ALL_FUNDS_AUTO_EXCHANGE;
  return (
    <div>
      <div className={styles.wrapper}>
        <div className={styles['landing-container']}>
          <div className={styles.header}>Auto-RMD Tool</div>
          <div className={styles.description}>
            Create a personalized program to annually recalculate and distribute
            your T. Rowe Price{' '}
            <span className={styles.bold}>Required Minimum Distribution</span>{' '}
            (RMD) from your eligible IRA investments{' '}
            <span className="orphan-line">every year.</span>
          </div>
          <div>
            <ModalLink
              link="View program benefits and restrictions"
              linkStyle="link"
              modalName={AUTO_RMD_TOOLD_AND_RESTRICTIONS_MODAL}
            />
          </div>
          <LandingErrorMessage errorType={errorType} hiddenRMDFundIRAs={hiddenRMDFundIRAs}/>
          <div className={styles['mobile-footer']}>
            This tool cannot be used to process deferred or missed RMDs; take
            your RMD multiple ways (eg, withdraw + reinvest); or fulfill RMDs
            from accounts that aren’t Rollover or Traditional IRAs.
          </div>
          <div className={styles['button-row']}>
            {!isSalesforceRep && (
              <a
                id="exit-auto-rmd"
                className={styles['no-underline']}
                href={`${config.url}/usis/rmd-tool`}
              >
                <Button>Exit</Button>
              </a>
            )}
            {errorType && autoCheck ? (
              <Button disabled>Get started</Button>
            ) : (
              <Link to={{ pathname: appendBasePath('/destination') }}>
                <Button
                  onClick={(e) => dispatch({ type: GET_STARTED_CLICKED })}
                  id="get-started"
                  type="primary"
                >
                  Get Started
                </Button>
              </Link>
            )}
          </div>
          <div className={styles['phone-header']}>
            <RMLConnection landingPage={true} phoneNumber={TOLL_FREE_NUMBER} />
            <div className={styles.footer}>
              This tool cannot be used to process deferred or missed RMDs; take
              your RMD multiple ways (eg, withdraw + reinvest); or fulfill RMDs
              from accounts that aren’t Rollover or Traditional IRAs.
            </div>
          </div>
        </div>
      </div>
      <div className={styles['lrn-number']}>
        3292270
      </div>
    </div>
  );
};
export default compose(withRouter)(LandingPage);
